import { ComponentProps } from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../utils';

const alertVariants = cva(
    'relative w-full rounded-2xl border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
    {
        variants: {
            variant: {
                default: 'bg-card text-card-foreground',
                destructive:
                    'border-red-300 bg-red-50 text-destructive [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-200 dark:*:data-[slot=alert-description]:text-red-200/90',
                success:
                    'bg-green-50 border-green-400 text-green-700 dark:border-green-900/50 dark:bg-green-950/20 dark:text-green-200',
                warning:
                    'bg-amber-50 border-amber-400 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-200',
                info: 'bg-blue-50 border-blue-600 text-blue-600 dark:border-blue-900/50 dark:bg-blue-950/20 dark:text-blue-200',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

function Alert({
    className,
    variant,
    ...props
}: ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
    return (
        <div
            data-slot="alert"
            role="alert"
            className={cn(alertVariants({ variant }), className)}
            {...props}
        />
    );
}

function AlertTitle({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="alert-title"
            className={cn(
                'col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight',
                className,
            )}
            {...props}
        />
    );
}

function AlertDescription({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="alert-description"
            className={cn(
                'col-start-2 grid justify-items-start gap-1 text-sm text-muted-foreground [&_p]:leading-relaxed',
                className,
            )}
            {...props}
        />
    );
}

export { Alert, AlertTitle, AlertDescription };
